import React, { useEffect, useState } from 'react';
import {
  Badge, Button, Card, Col, Form, Row,
} from 'react-bootstrap';
import moment from 'moment';
import { BasicPeopleLayout } from '../layout/PeopleLayout';
import { Icon, LoadingAnimation } from '../../common/components/Utilities';
import CandidateResource from '../../common/resource/CandidateResource';
import JobResource from '../../common/resource/JobResource';
import BasicPage from '../../common/layout/BasicPage';
import { CreateButton } from '../../common/components/Buttons';

/**
 * CandidatesAdminList - A React replacement for the old candidates/index.blade.php
 * Demonstrates the proper pattern for migrating Blade views to React with:
 * - Restful API endpoints using the repository pattern
 * - Proper use of existing React components and utilities
 * - Filter state management
 * - Pagination handling
 */
function CandidatesAdminList() {
  return (
    <BasicPeopleLayout>
      <CandidatesList />
    </BasicPeopleLayout>
  );
}

function CandidatesList() {
  const [loading, setLoading] = useState(true);
  const [candidates, setCandidates] = useState({ data: [], meta: {} });
  const [statuses, setStatuses] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [filters, setFilters] = useState({
    statuses: [],
    jobs: [],
    page: 1,
  });

  // Load statuses and jobs for filters
  const loadFilters = async () => {
    const [statusList, jobList] = await Promise.all([
      CandidateResource.statuses(),
      JobResource.index(),
    ]);
    setStatuses(statusList);
    setJobs(jobList);

    // Default to "in process" statuses
    const defaultStatuses = statusList
      .filter((status) => status.in_process)
      .map((status) => status.id);
    setFilters((prev) => ({ ...prev, statuses: defaultStatuses }));
  };

  // Load candidates with current filters
  const loadCandidates = async () => {
    setLoading(true);
    const params = {
      ...filters,
      with: ['job', 'status', 'location', 'recruiter', 'vacancy'],
      perPage: 15,
    };
    const response = await CandidateResource.index(params);
    setCandidates(response);
    setLoading(false);
  };

  useEffect(() => {
    loadFilters();
  }, []);

  useEffect(() => {
    if (statuses.length > 0) {
      loadCandidates();
    }
  }, [filters, statuses]);

  const toggleStatusFilter = (statusId) => {
    setFilters((prev) => {
      const newStatuses = prev.statuses.includes(statusId)
        ? prev.statuses.filter((id) => id !== statusId)
        : [...prev.statuses, statusId];
      return { ...prev, statuses: newStatuses, page: 1 };
    });
  };

  const toggleJobFilter = (jobId) => {
    setFilters((prev) => {
      const newJobs = prev.jobs.includes(jobId)
        ? prev.jobs.filter((id) => id !== jobId)
        : [...prev.jobs, jobId];
      return { ...prev, jobs: newJobs, page: 1 };
    });
  };

  const changePage = (newPage) => {
    setFilters((prev) => ({ ...prev, page: newPage }));
  };

  return (
    <BasicPage
      title="Candidates"
      subtitle="Recruitment Pipeline Management"
      action={<CreateButton href="/candidates/create" icon="user-plus">Add Candidate</CreateButton>}
    >
      <Row>
        <Col md={8}>
          <Card className="mb-4">
            <Card.Header>Candidates</Card.Header>
            <LoadingAnimation loading={loading} />
            {!loading && (
              <>
                <table className="table mb-0">
                  <thead>
                    <tr>
                      <th>Name</th>
                      <th>Status</th>
                      <th>Location</th>
                      <th className="text-center">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {candidates.data.length === 0 ? (
                      <tr>
                        <td colSpan="4" className="text-center text-muted">
                          No candidates found matching your filters.
                        </td>
                      </tr>
                    ) : (
                      candidates.data.map((candidate) => (
                        <CandidateRow key={candidate.id} candidate={candidate} />
                      ))
                    )}
                  </tbody>
                </table>

                {candidates.meta.last_page > 1 && (
                  <Card.Footer>
                    <Pagination
                      currentPage={candidates.meta.current_page}
                      lastPage={candidates.meta.last_page}
                      onPageChange={changePage}
                    />
                  </Card.Footer>
                )}
              </>
            )}
          </Card>
        </Col>

        <Col md={4}>
          <Card className="mb-4">
            <Card.Header>Status</Card.Header>
            <Card.Body>
              <Form>
                {statuses.map((status) => (
                  <Form.Check
                    key={status.id}
                    type="checkbox"
                    id={`status-${status.id}`}
                    label={(
                      <>
                        {status.title}
                        {' '}
                        <Badge variant="light">{status.candidates_count || 0}</Badge>
                      </>
                    )}
                    checked={filters.statuses.includes(status.id)}
                    onChange={() => toggleStatusFilter(status.id)}
                    className={status.in_process ? 'text-success' : 'text-muted'}
                  />
                ))}
              </Form>
            </Card.Body>
          </Card>

          <Card className="mb-4">
            <Card.Header>Role</Card.Header>
            <Card.Body>
              <Form>
                {jobs.map((job) => (
                  <Form.Check
                    key={job.id}
                    type="checkbox"
                    id={`job-${job.id}`}
                    label={job.title}
                    checked={filters.jobs.includes(job.id)}
                    onChange={() => toggleJobFilter(job.id)}
                  />
                ))}
              </Form>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </BasicPage>
  );
}

function CandidateRow({ candidate }) {
  const statusClass = candidate.status?.in_process ? '' : 'text-muted';

  return (
    <tr className={statusClass}>
      <td style={{ verticalAlign: 'middle', fontSize: '1.3em' }}>
        <a href={`/candidates/${candidate.uuid}/edit`}>{candidate.name}</a>
        <div className="text-muted small">{candidate.job?.title}</div>
      </td>

      <td style={{ verticalAlign: 'middle' }}>
        {candidate.status?.title}
        <div className="text-info">
          Since
          {' '}
          {moment(candidate.updated_at).format('MMMM Do YYYY')}
        </div>
      </td>

      <td style={{ verticalAlign: 'middle' }}>
        {candidate.location?.name}
      </td>

      <td style={{ verticalAlign: 'middle' }} className="text-center">
        {candidate.linkedin && (
          <a
            href={candidate.linkedin}
            target="_blank"
            rel="noreferrer"
            className="btn btn-sm btn-outline-success mx-1"
            title="LinkedIn Profile"
          >
            <Icon icon="linkedin" append="fab" />
          </a>
        )}
        {candidate.cv && (
          <a
            href={`/candidates/${candidate.uuid}/cv`}
            target="_blank"
            rel="noreferrer"
            className="btn btn-sm btn-outline-success mx-1"
            title="Download CV"
          >
            <Icon icon="file-download" />
          </a>
        )}
      </td>
    </tr>
  );
}

function Pagination({ currentPage, lastPage, onPageChange }) {
  const pages = [];
  const maxVisible = 5;

  let startPage = Math.max(1, currentPage - Math.floor(maxVisible / 2));
  const endPage = Math.min(lastPage, startPage + maxVisible - 1);

  if (endPage - startPage < maxVisible - 1) {
    startPage = Math.max(1, endPage - maxVisible + 1);
  }

  for (let i = startPage; i <= endPage; i += 1) {
    pages.push(i);
  }

  return (
    <nav>
      <ul className="pagination mb-0">
        <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
          <Button
            variant="link"
            className="page-link"
            onClick={() => onPageChange(currentPage - 1)}
            disabled={currentPage === 1}
          >
            Previous
          </Button>
        </li>

        {pages.map((page) => (
          <li key={page} className={`page-item ${currentPage === page ? 'active' : ''}`}>
            <Button
              variant="link"
              className="page-link"
              onClick={() => onPageChange(page)}
            >
              {page}
            </Button>
          </li>
        ))}

        <li className={`page-item ${currentPage === lastPage ? 'disabled' : ''}`}>
          <Button
            variant="link"
            className="page-link"
            onClick={() => onPageChange(currentPage + 1)}
            disabled={currentPage === lastPage}
          >
            Next
          </Button>
        </li>
      </ul>
    </nav>
  );
}

export default CandidatesAdminList;
